import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { MessageSquare } from "lucide-react"
import { PaginationState } from "@tanstack/react-table"
import { useGetThreadsQuery } from "../../../../redux/queries"
import { useUserRoles } from "../../../hooks"
import { DateRangePreset } from "../../activity/date-filter"
import { dateRangeToTimestamps } from "../../../utils/datetime"
import SmsThreadsTable from "./sms-table"

const PAGE_SIZE = 25

interface Props {
  brandId?: string | number
  loid?: string
  orgId?: string | number
}

const SmsTab = ({ brandId, loid, orgId }: Props) => {
  const { isSuperAdmin } = useUserRoles()
  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [datePreset, setDatePreset] = useState<DateRangePreset>("all")
  const [customDateStart, setCustomDateStart] = useState<number | null>(null)
  const [customDateEnd, setCustomDateEnd] = useState<number | null>(null)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })

  const queryParams = useMemo(() => {
    const { min: updated_min } = dateRangeToTimestamps(
      datePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )
    return {
      ...(!isSuperAdmin && brandId ? { brand_id: brandId } : {}),
      ...(loid ? { loid } : {}),
      ...(orgId ? { org_id: orgId } : {}),
      ...(debouncedSearch ? { name_like: debouncedSearch } : {}),
      ...(updated_min ? { updated_min } : {}),
      limit: PAGE_SIZE,
      skip: pagination.pageIndex * PAGE_SIZE,
      message_preview: "true" as const
    }
  }, [
    isSuperAdmin,
    brandId,
    loid,
    orgId,
    debouncedSearch,
    datePreset,
    customDateStart,
    customDateEnd,
    pagination.pageIndex
  ])

  const { data: threads = [], isLoading } = useGetThreadsQuery(queryParams)

  // Debounce search input — resets page when it fires
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPagination(prev => ({ ...prev, pageIndex: 0 }))
    }, 300)
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [searchInput])

  // Reset page when date filter or role params change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [datePreset, customDateStart, customDateEnd, brandId, loid, orgId])

  const handleDateChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      if (range === "custom" && startDate && endDate) {
        setDatePreset("custom")
        setCustomDateStart(startDate.getTime())
        setCustomDateEnd(endDate.getTime())
      } else {
        setDatePreset(range)
        setCustomDateStart(null)
        setCustomDateEnd(null)
      }
    },
    []
  )

  const handleClearDate = useCallback(() => {
    setDatePreset("all")
    setCustomDateStart(null)
    setCustomDateEnd(null)
  }, [])

  // Server-side pagination: RTK Query returns only the current page.
  // We estimate total count as: all pages before current are full, current may be partial.
  const hasMore = threads.length === PAGE_SIZE
  const totalRows = pagination.pageIndex * PAGE_SIZE + threads.length
  const pageCount = hasMore
    ? pagination.pageIndex + 2
    : pagination.pageIndex + 1

  if (isLoading && pagination.pageIndex === 0) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-10 rounded-lg bg-neutral-100 w-full" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-14 rounded-lg bg-neutral-50 border border-neutral-100"
          />
        ))}
      </div>
    )
  }

  if (!isLoading && !threads.length && pagination.pageIndex === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-blue-50">
          <MessageSquare className="w-7 h-7 text-blue-300" />
        </div>
        <p className="text-sm font-semibold text-neutral-600">
          No SMS conversations yet
        </p>
        <p className="text-xs text-neutral-400 max-w-xs">
          When leads start SMS conversations, their threads will appear here.
        </p>
      </div>
    )
  }

  return (
    <SmsThreadsTable
      threads={threads}
      isLoading={isLoading}
      totalRows={totalRows}
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      datePreset={datePreset}
      customDateStart={customDateStart}
      customDateEnd={customDateEnd}
      onDateChange={handleDateChange}
      onClearDate={handleClearDate}
    />
  )
}

export default SmsTab
